import { useRef, useState } from "react";
import { toast } from "sonner";
import { MapPin, Crosshair, Loader2, MousePointerClick } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";

const btn = "min-h-[44px] px-3 rounded-xl border text-sm font-medium inline-flex items-center justify-center gap-1.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF8A3D] disabled:opacity-60";

export async function pointToAddress(pt, prevAddress, onChange) {
  onChange({ address: prevAddress, ...pt });
  try { const { data } = await api.get("/geo/reverse", { params: pt }); onChange({ address: data.address, ...pt }); }
  catch (err) { toast.error(apiError(err.response?.data?.detail) || "Não foi possível obter o endereço deste ponto."); }
}

// Endereço do evento: autocomplete (combobox ARIA), localização atual ou ponto no mapa (reverse geocode).
export default function LocationPicker({ value, onChange, pickMode, setPickMode }) {
  const [sug, setSug] = useState([]);
  const [active, setActive] = useState(-1);
  const [busy, setBusy] = useState("");
  const timer = useRef(null);
  const open = sug.length > 0;

  const onType = (e) => {
    const v = e.target.value;
    onChange({ address: v, lat: null, lng: null });
    clearTimeout(timer.current);
    setActive(-1);
    if (v.trim().length < 4) return setSug([]);
    timer.current = setTimeout(async () => {
      try { const { data } = await api.get("/geo/autocomplete", { params: { q: v } }); setSug(data || []); } catch { setSug([]); }
    }, 450);
  };
  const pick = (s) => { onChange({ address: s.address, lat: s.lat, lng: s.lng }); setSug([]); setActive(-1); };
  const onKey = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => (a + 1) % sug.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => (a <= 0 ? sug.length - 1 : a - 1)); }
    else if (e.key === "Enter" && active >= 0) { e.preventDefault(); pick(sug[active]); }
    else if (e.key === "Escape") { setSug([]); setActive(-1); }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.error("Seu navegador não permite obter a localização.");
    setBusy("gps");
    navigator.geolocation.getCurrentPosition(async (p) => {
      await fillFromPoint({ lat: p.coords.latitude, lng: p.coords.longitude });
      setBusy("");
    }, () => { setBusy(""); toast.error("Não foi possível obter sua localização. Verifique a permissão do navegador."); }, { enableHighAccuracy: true, timeout: 12000 });
  };

  const fillFromPoint = (pt) => pointToAddress(pt, value.address, onChange);

  return (
    <div className="space-y-2">
      <label htmlFor="express-address" className="text-sm text-zinc-300 flex items-center gap-1.5"><MapPin className="h-4 w-4" aria-hidden="true" />Local do evento <span className="text-[#FF8A3D]" aria-hidden="true">*</span></label>
      <div className="relative">
        <Input id="express-address" data-testid="express-address" value={value.address} onChange={onType} onKeyDown={onKey}
          onBlur={() => { setSug([]); setActive(-1); }} autoComplete="off" required aria-required="true"
          role="combobox" aria-autocomplete="list" aria-expanded={open} aria-controls="express-address-list" aria-describedby="express-address-hint express-address-status"
          aria-activedescendant={active >= 0 ? `express-sug-${active}` : undefined}
          placeholder="Rua, número, bairro, cidade - UF" className="bg-[#09090B] border-white/15 rounded-xl h-11" />
        {open && (
          <ul id="express-address-list" role="listbox" aria-label="Sugestões de endereço" data-testid="express-address-suggestions"
            className="absolute z-[600] mt-1 w-full rounded-xl border border-white/15 bg-[#121217] shadow-2xl overflow-hidden">
            {sug.map((s, i) => (
              <li key={i} id={`express-sug-${i}`} role="option" aria-selected={active === i} data-testid={`express-address-sug-${i}`}
                onMouseDown={(e) => { e.preventDefault(); pick(s); }}
                className={`px-3 py-2.5 text-sm cursor-pointer min-h-[44px] ${active === i ? "bg-[#FF4D00]/20 text-white" : "text-zinc-200 hover:bg-white/5"}`}>{s.address}</li>
            ))}
          </ul>
        )}
      </div>
      <p id="express-address-hint" className="text-xs text-zinc-400">Digite e escolha uma sugestão, use sua localização ou marque o ponto no mapa.</p>
      <div className="grid grid-cols-2 gap-2">
        <button type="button" onClick={useMyLocation} disabled={busy === "gps"} data-testid="express-use-location" className={`${btn} border-white/15 text-zinc-100 hover:bg-white/5`}>
          {busy === "gps" ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Crosshair className="h-4 w-4" aria-hidden="true" />}Usar minha localização
        </button>
        <button type="button" onClick={() => setPickMode(!pickMode)} aria-pressed={pickMode} data-testid="express-pick-on-map"
          className={`${btn} ${pickMode ? "border-[#FF4D00] bg-[#FF4D00]/15 text-[#FFB27A]" : "border-white/15 text-zinc-100 hover:bg-white/5"}`}>
          <MousePointerClick className="h-4 w-4" aria-hidden="true" />{pickMode ? "Clique no mapa…" : "Marcar no mapa"}
        </button>
      </div>
      <p id="express-address-status" role="status" aria-live="polite" data-testid="express-coords-status" className={`text-xs ${value.lat != null ? "text-emerald-400" : "text-zinc-400"}`}>
        {value.lat != null ? "Local definido no mapa. Você pode arrastar o pino para ajustar." : pickMode ? "Modo de marcação ativo: clique no mapa para definir o local." : "Local ainda não marcado no mapa."}
      </p>
    </div>
  );
}
